/**
 * Section values → CSS custom properties.
 *
 * The browser recalculates styles itself, so changing a colour re-renders no React
 * component at all. The same call drives live preview while an editor types.
 *
 * The framework does NOT know which fields exist or what they are called — the app
 * supplies the mapping. See app/web/theme-vars.ts.
 */

/** Field name → CSS variable, optionally with a unit for numeric values. */
export type CssVarMap = Record<string, string | { cssVar: string; unit?: string }>;

export function applyCssVars(map: CssVarMap, data: Record<string, unknown>): void {
  const root = document.documentElement;

  for (const [field, target] of Object.entries(map)) {
    const value = data[field];
    if (value === undefined || value === null || value === '') continue;

    const cssVar = typeof target === 'string' ? target : target.cssVar;
    const unit = typeof target === 'string' ? '' : target.unit ?? '';
    root.style.setProperty(cssVar, `${value}${unit}`);
  }
}

/**
 * Applied synchronously before React renders, from a section's annotation defaults,
 * so the first paint already has the palette and the stylesheet needs no colour
 * literals of its own.
 */
export const bootstrapCssVars = (map: CssVarMap, defaults: Record<string, unknown>): void =>
  applyCssVars(map, defaults);
